import {
  grayColor,
  successColor,
  warningColor,
  dangerColor,
  whiteColor,
  defaultFont,
  hexToRgb
} from "assets/jss/material-dashboard-pro-react.jsx";

const borderColor = `rgba(${hexToRgb("#000000")}, 0.08)`;
const dividerColor = `rgba(${hexToRgb("#000000")}, 0.06)`;
const hoverColor = `rgba(${hexToRgb("#000000")}, 0.03)`;

const pillTones = {
  success: { bg: `rgba(${hexToRgb(successColor)}, 0.12)`, text: "#1b5e20" },
  warning: { bg: `rgba(${hexToRgb(warningColor)}, 0.14)`, text: "#e65100" },
  danger: { bg: `rgba(${hexToRgb(dangerColor)}, 0.12)`, text: "#b71c1c" },
  muted: { bg: `rgba(${hexToRgb(grayColor)}, 0.14)`, text: "#616161" },
  info: { bg: "rgba(2, 119, 189, 0.12)", text: "#01579b" }
};

const dataTableStyle = {
  card: {
    ...defaultFont,
    background: whiteColor,
    border: `1px solid ${borderColor}`,
    borderRadius: "12px",
    overflow: "hidden",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)"
  },
  headerBar: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    padding: "20px 24px",
    borderBottom: `1px solid ${borderColor}`,
    flexWrap: "wrap",
    gap: "16px"
  },
  headerTitle: {
    margin: 0,
    fontSize: "18px",
    fontWeight: 700,
    color: "#1e293b",
    letterSpacing: "-0.01em"
  },
  headerSubtitle: {
    margin: "4px 0 0",
    fontSize: "13.5px",
    color: "#64748b",
    lineHeight: "1.5"
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: "12px"
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "24px",
    padding: "12px 24px",
    background: "#ffffff",
    border: `1px solid ${borderColor}`,
    borderRadius: "12px",
    flexWrap: "nowrap",
    minHeight: "64px",
    marginBottom: "12px",
    boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
    width: "100%",
    boxSizing: "border-box"
  },
  toolbarLeft: {
    display: "flex",
    alignItems: "center",
    gap: "24px",
    flex: 1
  },
  toolbarRight: {
    display: "flex",
    alignItems: "center",
    gap: "8px"
  },
  searchWrapper: {
    position: "relative",
    width: "300px",
    flexShrink: 0
  },
  searchIcon: {
    position: "absolute",
    left: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    color: "#64748b",
    fontSize: "18px"
  },
  searchInput: {
    width: "100%",
    padding: "8px 12px 8px 40px",
    fontSize: "14px",
    borderRadius: "8px",
    border: `1px solid ${borderColor}`,
    background: "#f8fafc",
    transition: "all 0.2s ease",
    "&:focus": {
      outline: "none",
      borderColor: "#3b82f6",
      background: "#fff",
      boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)"
    }
  },
  toolbarItem: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "6px 12px",
    borderRadius: "6px",
    border: `1px solid ${borderColor}`,
    background: "#fff",
    fontSize: "13px",
    fontWeight: 500,
    color: "#475569",
    cursor: "pointer",
    transition: "all 0.2s",
    "&:hover": {
      background: "#f1f5f9",
      borderColor: "#cbd5e1"
    }
  },
  toolbarItemActive: {
    background: "#e2e8f0",
    borderColor: "#94a3b8",
    color: "#0f172a"
  },
  toolbarLabel: {
    fontSize: "12px",
    fontWeight: 600,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.05em"
  },
  tableWrap: {
    width: "100%",
    overflowX: "auto"
  },
  tableWrapCompact: {
    "& $td": {
      padding: "6px 16px",
      height: "36px"
    },
    "& $th": {
      padding: "8px 16px"
    }
  },
  table: {
    width: "100%",
    borderCollapse: "collapse"
  },
  th: {
    fontSize: "11px",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    fontWeight: 600,
    color: grayColor,
    textAlign: "left",
    padding: "12px 16px",
    borderBottom: `1px solid ${borderColor}`,
    whiteSpace: "nowrap",
    userSelect: "none"
  },
  thRight: {
    textAlign: "right"
  },
  thSortable: {
    cursor: "pointer",
    "&:hover": {
      color: "#616161"
    }
  },
  sortIcon: {
    fontSize: "14px",
    verticalAlign: "middle",
    marginLeft: "4px",
    opacity: 0.6
  },
  sortIconActive: {
    opacity: 1
  },
  row: {
    borderBottom: `1px solid ${dividerColor}`,
    transition: "background 0.15s ease",
    "&:last-child": {
      borderBottom: "none"
    },
    "&:hover": {
      background: hoverColor
    }
  },
  rowClickable: {
    cursor: "pointer"
  },
  td: {
    fontSize: "13px",
    color: "#333c4d",
    padding: "13px 16px",
    height: "48px",
    boxSizing: "border-box",
    verticalAlign: "middle"
  },
  tdRight: {
    textAlign: "right",
    fontVariantNumeric: "tabular-nums"
  },
  tdMono: {
    fontFamily: '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
    fontSize: "12px",
    color: grayColor
  },
  twoLinePrimary: {
    fontWeight: 500,
    fontSize: "13px",
    color: "#2c3345"
  },
  twoLineSecondary: {
    fontSize: "12px",
    color: grayColor,
    marginTop: "2px"
  },
  skeletonCell: {
    padding: "13px 16px",
    height: "48px",
    boxSizing: "border-box"
  },
  skeletonBar: {
    height: "10px",
    borderRadius: "4px",
    background: `rgba(${hexToRgb("#000000")}, 0.08)`,
    animation: "$pulse 1.4s ease-in-out infinite"
  },
  "@keyframes pulse": {
    "0%": { opacity: 0.6 },
    "50%": { opacity: 0.25 },
    "100%": { opacity: 0.6 }
  },
  emptyState: {
    padding: "48px 20px",
    textAlign: "center",
    color: grayColor,
    fontSize: "14px"
  },
  footerRow: {
    borderTop: `2px solid ${borderColor}`
  },
  footerLabelCell: {
    fontSize: "12px",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    fontWeight: 700,
    color: "#2c3345",
    padding: "12px 16px"
  },
  footerValueCell: {
    fontSize: "13px",
    fontWeight: 700,
    color: "#2c3345",
    padding: "12px 16px",
    textAlign: "right",
    fontVariantNumeric: "tabular-nums"
  },
  paginationBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 20px",
    borderTop: `1px solid ${borderColor}`,
    flexWrap: "wrap",
    gap: "12px"
  },
  paginationLeft: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "13px",
    color: grayColor
  },
  pageSizeSelect: {
    fontSize: "13px",
    padding: "4px 6px",
    borderRadius: "6px",
    border: `1px solid ${borderColor}`,
    background: whiteColor
  },
  paginationRight: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    fontSize: "13px",
    color: "#333c4d"
  },
  pageButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "28px",
    height: "28px",
    borderRadius: "6px",
    border: `1px solid ${borderColor}`,
    background: whiteColor,
    cursor: "pointer",
    "&:disabled": {
      opacity: 0.4,
      cursor: "not-allowed"
    }
  }
};

export { pillTones };
export default dataTableStyle;
